import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  HomeIcon,
  Menu,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
  X,
  AlertCircle,
} from "lucide-react";
import DirectoryList from "../DirectoryList";
import ParticipantsList from "../ParticipantsList";
import api from "../../services/api";

// Payment Verification Modal Component (keep it inline)
const PaymentVerificationModal = ({
  isOpen,
  onClose,
  API_BASE_URL,
  getAuthHeader,
}) => {
  const [orderId, setOrderId] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verificationError, setVerificationError] = useState("");

  const handleVerifyPayment = async () => {
    if (!orderId.trim()) {
      setVerificationError("Please enter an Order ID");
      return;
    }

    try {
      setVerifying(true);
      setVerificationError("");
      setVerificationResult(null);

      const headers = getAuthHeader();
      headers["Content-Type"] = "application/json";

      const response = await fetch(
        `${API_BASE_URL}/api/shurjo/verify-payment`,
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify({ order_id: orderId }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Verification failed");
      }

      setVerificationResult(result);
    } catch (error) {
      console.error("Payment verification error:", error);
      setVerificationError(error.message || "Failed to verify payment");
    } finally {
      setVerifying(false);
    }
  };

  const handleCloseModal = () => {
    onClose();
    setOrderId("");
    setVerificationResult(null);
    setVerificationError("");
  };

  const getStatusBadge = (spCode) => {
    switch (spCode) {
      case "1000":
        return <span className="badge bg-success">Success</span>;
      case "1001":
        return <span className="badge bg-danger">Failed</span>;
      case "1002":
        return <span className="badge bg-warning">Pending</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  const getStatusMessage = (spCode, spMessage) => {
    switch (spCode) {
      case "1000":
        return "Payment completed successfully";
      case "1001":
        return "Payment failed or cancelled";
      case "1002":
        return "Payment is pending";
      default:
        return spMessage || "Unknown status";
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Payment Verification</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleCloseModal}
              disabled={verifying}
            ></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label htmlFor="orderId" className="form-label text-black">
                Order ID
              </label>
              <input
                type="text"
                className="form-control"
                id="orderId"
                placeholder="Enter Order ID"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                disabled={verifying}
              />
            </div>

            {verificationError && (
              <div className="alert alert-danger">
                <AlertCircle size={18} className="me-2" />
                {verificationError}
              </div>
            )}

            {verificationResult && (
              <div className="card mt-3">
                <div className="card-body">
                  <h6 className="card-title d-flex align-items-center gap-2">
                    <CheckCircle size={20} className="text-success" />
                    Verification Result
                  </h6>

                  {verificationResult.verifyResponse && (
                    <>
                      <div className="mb-2">
                        <strong>Status:</strong>{" "}
                        {getStatusBadge(
                          verificationResult.verifyResponse.sp_code
                        )}
                      </div>
                      <div className="mb-2">
                        <strong>Message:</strong>{" "}
                        {getStatusMessage(
                          verificationResult.verifyResponse.sp_code,
                          verificationResult.verifyResponse.sp_message
                        )}
                      </div>
                      <div className="mb-2">
                        <strong>Order ID:</strong>{" "}
                        {verificationResult.verifyResponse.order_id}
                      </div>
                      <div className="mb-2">
                        <strong>Transaction ID:</strong>{" "}
                        {verificationResult.verifyResponse.sp_transaction_id ||
                          "N/A"}
                      </div>
                      <div className="mb-2">
                        <strong>Amount:</strong>{" "}
                        {verificationResult.verifyResponse.amount
                          ? `৳${verificationResult.verifyResponse.amount}`
                          : "N/A"}
                      </div>
                      <div className="mb-2">
                        <strong>Bank Transaction ID:</strong>{" "}
                        {verificationResult.verifyResponse.bank_trx_id || "N/A"}
                      </div>
                      <div className="mb-2">
                        <strong>Date:</strong>{" "}
                        {verificationResult.verifyResponse.date_time || "N/A"}
                      </div>
                    </>
                  )}

                  {verificationResult.message && (
                    <div className="mt-2">
                      <strong>Additional Info:</strong>{" "}
                      {verificationResult.message}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseModal}
              disabled={verifying}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleVerifyPayment}
              disabled={verifying || !orderId.trim()}
            >
              {verifying ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                  Verifying...
                </>
              ) : (
                "Verify Payment"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminPage = () => {
  // const [activeTab, setActiveTab] = useState("directory");
  const [activeTab, setActiveTab] = useState("participants");

  const [registrationActive, setRegistrationActive] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showPaymentVerificationModal, setShowPaymentVerificationModal] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();

  // Check screen width
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    if (
      user &&
      user.role !== "ADMIN" &&
      user.role !== "EDITOR" &&
      user.role !== "VIEWER"
    ) {
      navigate("/home");
    }
  }, [user, navigate]);

  const fetchRegistrationStatus = async () => {
    try {
      const data = await api.lookup.getRegistrationStatus();
      setRegistrationActive(data);
    } catch (err) {
      console.error("Error fetching registration status:", err);
    }
  };

  useEffect(() => {
    fetchRegistrationStatus();
  }, []);

  const toggleRegistrationStatus = async () => {
    try {
      setUpdatingStatus(true);
      const newStatus = !registrationActive;

      const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
      const url = `${API_BASE_URL}/api/lookup/status?active=${newStatus}`;

      const headers = getAuthHeader();
      headers["Content-Type"] = "application/json";

      const response = await fetch(url, {
        method: "PUT",
        headers: headers,
      });

      const contentType = response.headers.get("content-type");
      let result;

      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      } else {
        const text = await response.text();
        result = {
          message: text || "Status updated successfully",
          success: response.ok,
        };
      }

      if (!response.ok) {
        throw new Error(
          result.message || `HTTP ${response.status}: Failed to update status`
        );
      }

      setRegistrationActive(newStatus);
      showNotification(
        `Registration has been ${newStatus ? "ENABLED" : "DISABLED"} successfully!`,
        "success"
      );
    } catch (err) {
      console.error("Error updating registration status:", err);
      showNotification(
        `Failed to update registration status: ${err.message}`,
        "error"
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const showNotification = (message, type = "info") => {
    const existingNotification = document.querySelector(".custom-notification");
    if (existingNotification) {
      existingNotification.remove();
    }

    const notification = document.createElement("div");
    notification.className = `custom-notification alert alert-${
      type === "error" ? "danger" : type === "success" ? "success" : "info"
    }`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      min-width: 300px;
      max-width: 500px;
      padding: 15px;
      border-radius: 5px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideIn 0.3s ease-out;
    `;

    notification.innerHTML = `
      <div class="d-flex justify-content-between align-items-start">
        <div>
          <strong>${
            type === "error"
              ? "❌ Error"
              : type === "success"
              ? "✅ Success"
              : "ℹ️ Info"
          }</strong>
          <div class="mt-1">${message}</div>
        </div>
        <button class="btn btn-sm btn-link text-dark" onclick="this.parentElement.parentElement.remove()">
          ×
        </button>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5000);
  };

  const handleHome = () => {
    navigate("/home");
  };

  return (
    <div className="min-vh-100 bg-light">
      <PaymentVerificationModal
        isOpen={showPaymentVerificationModal}
        onClose={() => setShowPaymentVerificationModal(false)}
        API_BASE_URL={API_BASE_URL}
        getAuthHeader={getAuthHeader}
      />

      {/* Header */}
      <header className="bg-dark text-white py-3">
        <div className="container-fluid">
          <div className="row align-items-center">
            {/* Mobile Menu Button */}
            <div className="col-auto d-lg-none">
              <button
                className="btn btn-outline-light"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>

            {/* Title */}
            <div className="col">
              <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center">
                <h2 className="mb-0 me-3">Admin Dashboard</h2>
                <small className="text-light opacity-75">
                  Logged in as: <strong>{user?.username}</strong> ({user?.role})
                </small>
              </div>
            </div>

            {/* Desktop Actions */}
            <div className="col-auto d-none d-lg-block">
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={handleHome}
                >
                  <HomeIcon size={18} />
                  View Site
                </button>
                {user?.role === "ADMIN" && (
                  <button
                    className={`btn d-flex align-items-center gap-2 ${
                      registrationActive ? "btn-danger" : "btn-success"
                    }`}
                    onClick={toggleRegistrationStatus}
                    disabled={updatingStatus}
                  >
                    {registrationActive ? (
                      <ToggleLeft size={18} />
                    ) : (
                      <ToggleRight size={18} />
                    )}
                    {updatingStatus
                      ? "Updating..."
                      : registrationActive
                      ? "Disable"
                      : "Enable"}
                  </button>
                )}
                {user?.role === "ADMIN" && (
                  <button
                    className="btn btn-info d-flex align-items-center gap-2 text-white"
                    onClick={() => setShowPaymentVerificationModal(true)}
                  >
                    <CheckCircle size={18} />
                    Payment Verification
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Actions (Collapsible) */}
          {isMobileMenuOpen && (
            <div className="row mt-3 d-lg-none">
              <div className="col-12">
                <div className="d-flex flex-column gap-2">
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2 justify-content-center"
                    onClick={handleHome}
                  >
                    <HomeIcon size={18} />
                    View Site
                  </button>
                  {user?.role === "ADMIN" && (
                    <button
                      className={`btn d-flex align-items-center gap-2 justify-content-center ${
                        registrationActive ? "btn-danger" : "btn-success"
                      }`}
                      onClick={toggleRegistrationStatus}
                      disabled={updatingStatus}
                    >
                      {registrationActive ? (
                        <ToggleLeft size={18} />
                      ) : (
                        <ToggleRight size={18} />
                      )}
                      {updatingStatus
                        ? "Updating..."
                        : registrationActive
                        ? "Disable Registration"
                        : "Enable Registration"}
                    </button>
                  )}
                  {user?.role === "ADMIN" && (
                    <button
                      className="btn btn-info d-flex align-items-center gap-2 justify-content-center text-white"
                      onClick={() => setShowPaymentVerificationModal(true)}
                    >
                      <CheckCircle size={18} />
                      Payment Verification
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="row mt-3">
            <div className="col-12">
              <div className="d-flex gap-2">
                <button
                  className={`btn d-flex align-items-center justify-content-between flex-fill ${
                    activeTab === "directory" ? "btn-search" : "btn-success"
                  }`}
                  onClick={() => setActiveTab("directory")}
                  style={{ minHeight: "60px" }}
                >
                  <div
                    className={`${
                      activeTab === "directory" ? "fw-bolder text-xl" : ""
                    }`}
                  >
                    Directory
                  </div>
                </button>
                <button
                  className={`btn d-flex align-items-center justify-content-between flex-fill ${
                    activeTab === "participants" ? "btn-search" : "btn-success"
                  }`}
                  onClick={() => setActiveTab("participants")}
                  style={{ minHeight: "60px" }}
                >
                  <div
                    className={`${
                      activeTab === "participants" ? "fw-bolder text-xl" : ""
                    }`}
                  >
                    Participants
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-fluid py-3">
        {activeTab === "directory" ? (
          <DirectoryList
            user={user}
            API_BASE_URL={API_BASE_URL}
            getAuthHeader={getAuthHeader}
            logout={logout}
          />
        ) : (
          <ParticipantsList
            user={user}
            API_BASE_URL={API_BASE_URL}
            getAuthHeader={getAuthHeader}
            logout={logout}
          />
        )}
      </main>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default AdminPage;



// import {
//   Download,
//   Edit2,
//   Eye,
//   Filter,
//   Home as HomeIcon,
//   Menu,
//   RefreshCw,
//   Search,
//   ToggleLeft,
//   ToggleRight,
//   Trash2,
//   X,
//   AlertCircle,
//   CheckCircle,
// } from "lucide-react";
// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import * as XLSX from "xlsx";
// import { useAuth } from "../../context/AuthContext";
// import api from "../../services/api";

// // Payment Verification Modal Component - MOVE THIS OUTSIDE AdminPage
// const PaymentVerificationModal = ({
//   isOpen,
//   onClose,
//   API_BASE_URL,
//   getAuthHeader,
//   fetchParticipants,
// }) => {
//   const [orderId, setOrderId] = useState("");
//   const [verifying, setVerifying] = useState(false);
//   const [verificationResult, setVerificationResult] = useState(null);
//   const [verificationError, setVerificationError] = useState("");

//   const handleVerifyPayment = async () => {
//     if (!orderId.trim()) {
//       setVerificationError("Please enter an Order ID");
//       return;
//     }

//     try {
//       setVerifying(true);
//       setVerificationError("");
//       setVerificationResult(null);

//       const headers = getAuthHeader();
//       headers["Content-Type"] = "application/json";

//       const response = await fetch(
//         `${API_BASE_URL}/api/shurjo/verify-payment`,
//         {
//           method: "POST",
//           headers: headers,
//           body: JSON.stringify({ order_id: orderId }),
//         }
//       );

//       const result = await response.json();

//       if (!response.ok) {
//         throw new Error(result.message || "Verification failed");
//       }

//       setVerificationResult(result);

//       // Refresh participant data if verification was successful
//       if (result.success || result.verifyResponse?.sp_code === "1000") {
//         setTimeout(() => {
//           fetchParticipants();
//         }, 1500);
//       }
//     } catch (error) {
//       console.error("Payment verification error:", error);
//       setVerificationError(error.message || "Failed to verify payment");
//     } finally {
//       setVerifying(false);
//     }
//   };

//   const handleCloseModal = () => {
//     onClose();
//     setOrderId("");
//     setVerificationResult(null);
//     setVerificationError("");
//   };

//   const getStatusBadge = (spCode) => {
//     switch (spCode) {
//       case "1000":
//         return <span className="badge bg-success">Success</span>;
//       case "1001":
//         return <span className="badge bg-danger">Failed</span>;
//       case "1002":
//         return <span className="badge bg-warning">Pending</span>;
//       default:
//         return <span className="badge bg-secondary">Unknown</span>;
//     }
//   };

//   const getStatusMessage = (spCode, spMessage) => {
//     switch (spCode) {
//       case "1000":
//         return "Payment completed successfully";
//       case "1001":
//         return "Payment failed or cancelled";
//       case "1002":
//         return "Payment is pending";
//       default:
//         return spMessage || "Unknown status";
//     }
//   };

//   if (!isOpen) return null;

//   return (
//     <div
//       className="modal fade show d-block"
//       style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
//     >
//       <div className="modal-dialog modal-dialog-centered">
//         <div className="modal-content">
//           <div className="modal-header">
//             <h5 className="modal-title">Payment Verification</h5>
//             <button
//               type="button"
//               className="btn-close"
//               onClick={handleCloseModal}
//               disabled={verifying}
//             ></button>
//           </div>
//           <div className="modal-body">
//             <div className="mb-3">
//               <label htmlFor="orderId" className="form-label text-black">
//                 Order ID
//               </label>
//               <input
//                 type="text"
//                 className="form-control"
//                 id="orderId"
//                 placeholder="Enter Order ID"
//                 value={orderId}
//                 onChange={(e) => setOrderId(e.target.value)}
//                 disabled={verifying}
//               />
              
//             </div>

//             {verificationError && (
//               <div className="alert alert-danger">
//                 <AlertCircle size={18} className="me-2" />
//                 {verificationError}
//               </div>
//             )}

//             {verificationResult && (
//               <div className="card mt-3">
//                 <div className="card-body">
//                   <h6 className="card-title d-flex align-items-center gap-2">
//                     <CheckCircle size={20} className="text-success" />
//                     Verification Result
//                   </h6>

//                   {verificationResult.verifyResponse && (
//                     <>
//                       <div className="mb-2">
//                         <strong>Status:</strong>{" "}
//                         {getStatusBadge(
//                           verificationResult.verifyResponse.sp_code
//                         )}
//                       </div>
//                       <div className="mb-2">
//                         <strong>Message:</strong>{" "}
//                         {getStatusMessage(
//                           verificationResult.verifyResponse.sp_code,
//                           verificationResult.verifyResponse.sp_message
//                         )}
//                       </div>
//                       <div className="mb-2">
//                         <strong>Order ID:</strong>{" "}
//                         {verificationResult.verifyResponse.order_id}
//                       </div>
//                       <div className="mb-2">
//                         <strong>Transaction ID:</strong>{" "}
//                         {verificationResult.verifyResponse.sp_transaction_id ||
//                           "N/A"}
//                       </div>
//                       <div className="mb-2">
//                         <strong>Amount:</strong>{" "}
//                         {verificationResult.verifyResponse.amount
//                           ? `৳${verificationResult.verifyResponse.amount}`
//                           : "N/A"}
//                       </div>
//                       <div className="mb-2">
//                         <strong>Bank Transaction ID:</strong>{" "}
//                         {verificationResult.verifyResponse.bank_trx_id || "N/A"}
//                       </div>
//                       <div className="mb-2">
//                         <strong>Date:</strong>{" "}
//                         {verificationResult.verifyResponse.date_time || "N/A"}
//                       </div>
//                     </>
//                   )}

//                   {verificationResult.message && (
//                     <div className="mt-2">
//                       <strong>Additional Info:</strong>{" "}
//                       {verificationResult.message}
//                     </div>
//                   )}
//                 </div>
//               </div>
//             )}
//           </div>
//           <div className="modal-footer">
//             <button
//               type="button"
//               className="btn btn-secondary"
//               onClick={handleCloseModal}
//               disabled={verifying}
//             >
//               Cancel
//             </button>
//             <button
//               type="button"
//               className="btn btn-primary"
//               onClick={handleVerifyPayment}
//               disabled={verifying || !orderId.trim()}
//             >
//               {verifying ? (
//                 <>
//                   <span
//                     className="spinner-border spinner-border-sm me-2"
//                     role="status"
//                   ></span>
//                   Verifying...
//                 </>
//               ) : (
//                 <>
                  
//                   Verify Payment
//                 </>
//               )}
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const AdminPage = () => {
//   const [participants, setParticipants] = useState([]);
//   const [filteredParticipants, setFilteredParticipants] = useState([]);
//   const [directories, setDirectories] = useState([]);
//   const [filteredDirectories, setFilteredDirectories] = useState([]);
//   const [batchFilter, setBatchFilter] = useState("");
//   const [bloodGroupFilter, setBloodGroupFilter] = useState("");
//   const [directorySearchTerm, setDirectorySearchTerm] = useState("");
//   const [participantSearchTerm, setParticipantSearchTerm] = useState("");
//   const [loading, setLoading] = useState(true);
//   const [activeTab, setActiveTab] = useState("directory");
//   const [registrationActive, setRegistrationActive] = useState(null);
//   const [updatingStatus, setUpdatingStatus] = useState(false);
//   const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
//   const [isFilterOpen, setIsFilterOpen] = useState(false);
//   const [refreshingDirectory, setRefreshingDirectory] = useState(false);
//   const [refreshingParticipants, setRefreshingParticipants] = useState(false);

//   const [showPaymentVerificationModal, setShowPaymentVerificationModal] =
//     useState(false);

//   const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
//   const navigate = useNavigate();
//   const { user, logout, getAuthHeader } = useAuth();

//   // Check screen width for responsive behavior
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
//   const [isTablet, setIsTablet] = useState(window.innerWidth < 1024);

//   useEffect(() => {
//     const handleResize = () => {
//       setIsMobile(window.innerWidth < 768);
//       setIsTablet(window.innerWidth < 1024);
//       if (window.innerWidth > 768) {
//         setIsMobileMenuOpen(false);
//       }
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   React.useEffect(() => {
//     if (
//       user &&
//       user.role !== "ADMIN" &&
//       user.role !== "EDITOR" &&
//       user.role !== "VIEWER"
//     ) {
//       navigate("/home");
//     }
//   }, [user, navigate]);

//   const fetchParticipants = async () => {
//     setRefreshingParticipants(true);
//     try {
//       const data = await api.registration.getAll();
//       setParticipants(data);
//       setFilteredParticipants(data);
//     } catch (error) {
//       console.error("Error fetching participants:", error);
//       if (error.message.includes("Failed to fetch")) {
//         alert("Cannot connect to server. Please check your connection.");
//       }
//     } finally {
//       setRefreshingParticipants(false);
//       setLoading(false);
//     }
//   };

//   const fetchDirectories = async () => {
//     setRefreshingDirectory(true);
//     try {
//       const data = await api.directory.getAll();
//       setDirectories(data);
//       setFilteredDirectories(data);
//     } catch (error) {
//       console.error("Error fetching directories:", error);
//       if (error.message.includes("Failed to fetch")) {
//         alert("Cannot connect to server. Please check your connection.");
//       }
//     } finally {
//       setRefreshingDirectory(false);
//       setLoading(false);
//     }
//   };

//   const fetchRegistrationStatus = async () => {
//     try {
//       const data = await api.lookup.getRegistrationStatus();
//       setRegistrationActive(data);
//     } catch (err) {
//       console.error("Error fetching registration status:", err);
//     }
//   };

//   const refreshAllData = async () => {
//     setLoading(true);
//     try {
//       await Promise.all([
//         fetchParticipants(),
//         fetchDirectories(),
//         fetchRegistrationStatus(),
//       ]);
//     } catch (error) {
//       console.error("Error refreshing data:", error);
//     }
//   };

//   const refreshDirectoryData = async () => {
//     await fetchDirectories();
//     await fetchRegistrationStatus();
//   };

//   const refreshParticipantsData = async () => {
//     await fetchParticipants();
//     await fetchRegistrationStatus();
//   };

//   useEffect(() => {
//     refreshAllData();
//   }, []);

//   useEffect(() => {
//     const lowerSearch = participantSearchTerm.toLowerCase();
//     const filtered = participants.filter((p) =>
//       Object.values({
//         name: p.name,
//         batch: p.batch,
//         mobile: p.mobile,
//         email: p.email,
//         tshirtSize: p.tshirtSize,
//         adults: p.accompany.adult,
//         childAbove10: p.accompany.childAbove10,
//         childBelow10: p.accompany.childBelow10,
//         totalAmount: p.totalAmount,
//         registrationDate: p.registrationDate,
//         orderId: p.orderId,
//         transactionId: p.transactionId,
//         bankTransactionId: p.bankTransactionId,
//         paymentStatus: p.paymentStatus,
//         paymentGatewayStatus: p.paymentGatewayStatus,
//         PaymentMethod: p.paymentMethod,
//       })
//         .join(" ")
//         .toLowerCase()
//         .includes(lowerSearch)
//     );

//     setFilteredParticipants(filtered);
//   }, [participantSearchTerm, participants]);

//   useEffect(() => {
//     let filtered = directories;

//     if (batchFilter) {
//       filtered = filtered.filter((d) => d.batch === batchFilter);
//     }

//     if (bloodGroupFilter) {
//       filtered = filtered.filter((d) => d.bloodGroup === bloodGroupFilter);
//     }

//     if (directorySearchTerm) {
//       const lowerSearch = directorySearchTerm.toLowerCase();
//       filtered = filtered.filter((d) =>
//         Object.values({
//           name: d.name,
//           batch: d.batch,
//           mobile: d.mobile,
//           email: d.email,
//           profession: d.profession,
//           officeAddress: d.officeAddress,
//           permanentAddress: d.permanentAddress,
//           bloodGroup: d.bloodGroup,
//         })
//           .join(" ")
//           .toLowerCase()
//           .includes(lowerSearch)
//       );
//     }

//     setFilteredDirectories(filtered);
//   }, [directorySearchTerm, directories, batchFilter, bloodGroupFilter]);

//   // const toggleRegistrationStatus = async () => {
//   //   try {
//   //     setUpdatingStatus(true);
//   //     const newStatus = !registrationActive;

//   //     await api.lookup.updateRegistrationStatus(newStatus);
//   //     setRegistrationActive(newStatus);
//   //     alert(`Registration has been turned ${newStatus ? "ON" : "OFF"}`);
//   //   } catch (err) {
//   //     console.error("Error updating registration status:", err);
//   //     alert("Failed to update registration status.");
//   //   } finally {
//   //     setUpdatingStatus(false);
//   //   }
//   // };
//   const toggleRegistrationStatus = async () => {
//     try {
//       setUpdatingStatus(true);
//       const newStatus = !registrationActive;

//       console.log("Updating registration status to:", newStatus);

//       // Call the API directly to handle both JSON and text responses
//       const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
//       const url = `${API_BASE_URL}/api/lookup/status?active=${newStatus}`;

//       console.log("API URL:", url);

//       const headers = getAuthHeader();
//       headers["Content-Type"] = "application/json";

//       const response = await fetch(url, {
//         method: "PUT",
//         headers: headers,
//       });

//       console.log("Response status:", response.status);
//       console.log(
//         "Response headers:",
//         Object.fromEntries(response.headers.entries())
//       );

//       // Check if response is JSON or text
//       const contentType = response.headers.get("content-type");
//       let result;

//       if (contentType && contentType.includes("application/json")) {
//         result = await response.json();
//       } else {
//         const text = await response.text();
//         console.log("Text response:", text);
//         // If it's not JSON, create a simple result object
//         result = {
//           message: text || "Status updated successfully",
//           success: response.ok,
//         };
//       }

//       if (!response.ok) {
//         throw new Error(
//           result.message || `HTTP ${response.status}: Failed to update status`
//         );
//       }

//       setRegistrationActive(newStatus);

//       // Show success message
//       const successMessage =
//         result.message ||
//         `Registration has been ${
//           newStatus ? "ENABLED" : "DISABLED"
//         } successfully!`;
//       showNotification(successMessage, "success");
//     } catch (err) {
//       console.error("Error updating registration status:", err);
//       showNotification(
//         `Failed to update registration status: ${err.message}`,
//         "error"
//       );
//     } finally {
//       setUpdatingStatus(false);
//     }
//   };

//   const showNotification = (message, type = "info") => {
//     // Remove any existing notification
//     const existingNotification = document.querySelector(".custom-notification");
//     if (existingNotification) {
//       existingNotification.remove();
//     }

//     // Create notification element
//     const notification = document.createElement("div");
//     notification.className = `custom-notification alert alert-${
//       type === "error" ? "danger" : type === "success" ? "success" : "info"
//     }`;
//     notification.style.cssText = `
//       position: fixed;
//       top: 20px;
//       right: 20px;
//       z-index: 9999;
//       min-width: 300px;
//       max-width: 500px;
//       padding: 15px;
//       border-radius: 5px;
//       box-shadow: 0 4px 12px rgba(0,0,0,0.15);
//       animation: slideIn 0.3s ease-out;
//     `;

//     notification.innerHTML = `
//       <div class="d-flex justify-content-between align-items-start">
//         <div>
//           <strong>${
//             type === "error"
//               ? "❌ Error"
//               : type === "success"
//               ? "✅ Success"
//               : "ℹ️ Info"
//           }</strong>
//           <div class="mt-1">${message}</div>
//         </div>
//         <button class="btn btn-sm btn-link text-dark" onclick="this.parentElement.parentElement.remove()">
//           ×
//         </button>
//       </div>
//     `;

//     document.body.appendChild(notification);

//     // Auto remove after 5 seconds
//     setTimeout(() => {
//       if (notification.parentElement) {
//         notification.remove();
//       }
//     }, 5000);
//   };

//   // Add CSS animation
//   const style = document.createElement("style");
//   style.textContent = `
//     @keyframes slideIn {
//       from {
//         transform: translateX(100%);
//         opacity: 0;
//       }
//       to {
//         transform: translateX(0);
//         opacity: 1;
//       }
//     }
//   `;
//   document.head.appendChild(style);

//   const downloadExcel = () => {
//     const ws = XLSX.utils.json_to_sheet(
//       filteredParticipants.map((p) => ({
//         ID: p.id,
//         Name: p.name,
//         Batch: p.batch,
//         Mobile: p.mobile,
//         Email: p.email,
//         "T-Shirt Size": p.tshirtSize,
//         Adults: p.accompany.adult,
//         "Children (10+)": p.accompany.childAbove10,
//         "Children (10-)": p.accompany.childBelow10,
//         "Total Amount": p.totalAmount,
//         "Registration Date": p.registrationDate,
//         "Order ID": p.orderId,
//         "Transaction ID": p.transactionId,
//         "Bank Transaction ID": p.bankTransactionId,
//         "Payment Status": p.paymentStatus,
//         "Payment Gateway Status": p.paymentGatewayStatus,
//         "Payment Method": p.paymentMethod,
//       }))
//     );
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, "Participants");
//     XLSX.writeFile(
//       wb,
//       `participants_${new Date().toISOString().split("T")[0]}.xlsx`
//     );
//   };

//   const downloadDirectoryExcel = () => {
//     const ws = XLSX.utils.json_to_sheet(
//       filteredDirectories.map((d) => ({
//         ID: d.id,
//         Name: d.name,
//         Batch: d.batch,
//         Mobile: d.mobile,
//         Email: d.email,
//         Profession: d.profession,
//         "Office Address": d.officeAddress,
//         "Permanent Address": d.permanentAddress,
//         "Blood Group": d.bloodGroup,
//         "Image URL": d.imageUrl ? `${API_BASE_URL}${d.imageUrl}` : "N/A",
//       }))
//     );
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, "Directory");
//     XLSX.writeFile(
//       wb,
//       `directory_${new Date().toISOString().split("T")[0]}.xlsx`
//     );
//   };

//   const ImageWithActions = ({ imageUrl, name, altText }) => {
//     const [showActions, setShowActions] = useState(false);

//     const handleViewImage = () => {
//       if (imageUrl) {
//         window.open(`${API_BASE_URL}${imageUrl}`, "_blank");
//       }
//     };

//     const handleDownloadImage = async () => {
//       if (imageUrl) {
//         try {
//           const response = await fetch(`${API_BASE_URL}${imageUrl}`);
//           const blob = await response.blob();
//           const url = window.URL.createObjectURL(blob);
//           const a = document.createElement("a");
//           a.href = url;
//           a.download = `${name || "image"}.jpg`;
//           document.body.appendChild(a);
//           a.click();
//           window.URL.revokeObjectURL(url);
//           document.body.removeChild(a);
//         } catch (error) {
//           console.error("Error downloading image:", error);
//           alert("Failed to download image");
//         }
//       }
//     };

//     return (
//       <div
//         className="position-relative d-inline-block"
//         onMouseEnter={() => setShowActions(true)}
//         onMouseLeave={() => setShowActions(false)}
//         onClick={() => setShowActions(true)}
//       >
//         {imageUrl ? (
//           <img
//             src={`${API_BASE_URL}${imageUrl}`}
//             alt={altText || name}
//             className="rounded-circle"
//             style={{
//               width: isMobile ? "50px" : isTablet ? "40px" : "60px",
//               height: isMobile ? "50px" : isTablet ? "40px" : "60px",
//               objectFit: "cover",
//               cursor: "pointer",
//             }}
//             onError={(e) => {
//               e.target.onerror = null;
//               e.target.src =
//                 "https://ui-avatars.com/api/?name=" +
//                 encodeURIComponent(name) +
//                 "&background=random&color=fff&size=50";
//             }}
//           />
//         ) : (
//           <div
//             className="rounded-circle d-flex align-items-center justify-content-center"
//             style={{
//               width: isMobile ? "50px" : isTablet ? "40px" : "60px",
//               height: isMobile ? "50px" : isTablet ? "40px" : "60px",
//               backgroundColor: "#6c757d",
//               color: "white",
//               fontSize: "1rem",
//               cursor: "pointer",
//             }}
//           >
//             {name?.charAt(0).toUpperCase()}
//           </div>
//         )}

//         {imageUrl && showActions && (
//           <div
//             className="position-absolute top-50 start-50 translate-middle d-flex gap-1"
//             style={{ zIndex: 10 }}
//           >
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 handleViewImage();
//               }}
//               className="btn btn-sm btn-light shadow-sm"
//               title="View Image"
//               style={{ padding: "2px 6px" }}
//             >
//               <Eye size={12} />
//             </button>
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 handleDownloadImage();
//               }}
//               className="btn btn-sm btn-light shadow-sm"
//               title="Download Image"
//               style={{ padding: "2px 6px" }}
//             >
//               <Download size={12} />
//             </button>
//           </div>
//         )}

//         {showActions && imageUrl && (
//           <div
//             className="position-absolute top-0 start-0 w-100 h-100 rounded-circle"
//             style={{
//               backgroundColor: "rgba(0, 0, 0, 0.5)",
//               zIndex: 5,
//             }}
//           />
//         )}
//       </div>
//     );
//   };

//   const handleHome = () => {
//     navigate("/home");
//   };

//   const handleLogout = () => {
//     logout();
//     navigate("/login");
//   };

//   const handleViewDir = async (id) => {
//     navigate(`/directory-details/${id}`);
//   };

//   const handleViewPart = async (id) => {
//     navigate(`/registration-details/${id}`);
//   };

//   const handleEditPart = async (id) => {
//     navigate(`/edit-registration/${id}`);
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm("Are you sure you want to delete this participant?"))
//       return;

//     try {
//       const headers = getAuthHeader();
//       headers["Content-Type"] = "application/json";

//       const response = await fetch(`${API_BASE_URL}/api/registrations/${id}`, {
//         method: "DELETE",
//         headers: headers,
//       });

//       if (response.status === 401) {
//         logout();
//         navigate("/login");
//         return;
//       }

//       const result = await response.json();
//       if (!response.ok) throw new Error(result.message || "Failed to delete");

//       alert(result.message);
//       setParticipants(participants.filter((p) => p.id !== id));
//       setFilteredParticipants(filteredParticipants.filter((p) => p.id !== id));
//     } catch (error) {
//       console.error("Delete failed:", error);
//       alert("Failed to delete participant: " + error.message);
//     }
//   };

//   const handleDeleteDirectory = async (id) => {
//     if (
//       !window.confirm("Are you sure you want to delete this directory entry?")
//     )
//       return;

//     try {
//       const response = await fetch(`${API_BASE_URL}/api/directories/${id}`, {
//         method: "DELETE",
//         headers: getAuthHeader(),
//       });

//       if (response.status === 401) {
//         logout();
//         navigate("/login");
//         return;
//       }

//       if (!response.ok) throw new Error("Failed to delete directory entry");

//       setDirectories(directories.filter((d) => d.id !== id));
//       setFilteredDirectories(filteredDirectories.filter((d) => d.id !== id));

//       alert("Directory entry deleted successfully");
//     } catch (error) {
//       console.error("Delete failed:", error);
//       alert("Failed to delete directory entry: " + error.message);
//     }
//   };

//   if (loading && participants.length === 0 && directories.length === 0) {
//     return (
//       <div
//         style={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           height: "100vh",
//           background: "#1a3625",
//         }}
//       >
//         <div className="text-white text-center">
//           <div className="spinner-border mb-3" role="status">
//             <span className="visually-hidden">Loading...</span>
//           </div>
//           <h4>Loading Admin Dashboard...</h4>
//         </div>
//       </div>
//     );
//   }

//   const completedPayments = participants.filter(
//     (p) => p.paymentStatus === "Success" || p.paymentStatus === "Paid"
//   ).length;

//   const uniqueBatches = [
//     ...new Set(directories.map((d) => d.batch).filter(Boolean)),
//   ];
//   const uniqueBloodGroups = [
//     ...new Set(directories.map((d) => d.bloodGroup).filter(Boolean)),
//   ];

//   const getDirectoryTableHeaders = () => {
//     if (isMobile) {
//       return [
//         { key: "info", label: "Info", className: "col-6" },
//         { key: "actions", label: "Actions", className: "col-6" },
//       ];
//     } else if (isTablet) {
//       return [
//         { key: "photo", label: "Photo", className: "col-2" },
//         { key: "basic", label: "Basic Info", className: "col-4" },
//         { key: "contact", label: "Contact", className: "col-3" },
//         { key: "actions", label: "Actions", className: "col-3" },
//       ];
//     }
//     return [
//       { key: "photo", label: "Photo", className: "" },
//       { key: "basic", label: "Basic Info", className: "" },
//       { key: "contact", label: "Contact", className: "" },
//       { key: "professional", label: "Professional Info", className: "" },
//       { key: "blood", label: "Blood Group", className: "" },
//       { key: "actions", label: "Actions", className: "" },
//     ];
//   };

//   const getParticipantsTableHeaders = () => {
//     if (isMobile) {
//       return [
//         { key: "participant", label: "Participant", className: "col-6" },
//         { key: "actions", label: "Actions", className: "col-6" },
//       ];
//     } else if (isTablet) {
//       return [
//         { key: "participant", label: "Participant", className: "col-3" },
//         { key: "payment", label: "Payment", className: "col-3" },
//         { key: "amount", label: "Amount", className: "col-2" },
//         { key: "actions", label: "Actions", className: "col-4" },
//       ];
//     }
//     return [
//       { key: "participant", label: "Participant", className: "" },
//       { key: "batch", label: "Batch", className: "" },
//       { key: "contact", label: "Contact", className: "" },
//       { key: "payment", label: "Payment Status", className: "" },
//       { key: "transaction", label: "Transaction Info", className: "" },
//       { key: "method", label: "Payment Method", className: "" },
//       { key: "amount", label: "Amount", className: "" },
//       { key: "date", label: "Date", className: "" },
//       { key: "actions", label: "Actions", className: "" },
//     ];
//   };

//   const MobileDirectoryRow = ({ person }) => (
//     <div className="card mb-3">
//       <div className="card-body">
//         <div className="row">
//           <div className="col-8">
//             <div className="d-flex align-items-start gap-3">
//               <ImageWithActions
//                 imageUrl={person.imageUrl}
//                 name={person.name}
//                 altText={person.name}
//               />
//               <div>
//                 <h6
//                   className="mb-1 text-truncate"
//                   style={{ maxWidth: "170px" }}
//                 >
//                   {person.name}
//                 </h6>
//                 <span className="badge bg-primary mb-2">{person.batch}</span>
//                 <div className="small">
//                   <div>{person.mobile}</div>
//                   <div className="text-truncate" style={{ maxWidth: "200px" }}>
//                     {person.email}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className="col-4">
//             <div className="d-flex flex-column gap-2">
//               {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//                 <button
//                   onClick={() => handleDeleteDirectory(person.id)}
//                   className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
//                   title="Delete"
//                 >
//                   <Trash2 size={14} />
//                   <span className="d-none d-sm-inline ms-1">Delete</span>
//                 </button>
//               )}
//               <button
//                 onClick={() => handleViewDir(person.id)}
//                 className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
//                 title="View"
//               >
//                 <Eye size={14} />
//               </button>
//             </div>
//           </div>
//         </div>
//         {person.profession && (
//           <div className="mt-3 small">
//             <strong>Profession:</strong> {person.profession}
//           </div>
//         )}
//       </div>
//     </div>
//   );

//   const TabletDirectoryRow = ({ person }) => (
//     <tr>
//       {/* <td>
//         {person.imageUrl ? (
//           <img
//             src={`${API_BASE_URL}${person.imageUrl}`}
//             alt={person.name}
//             className="rounded-circle"
//             style={{ width: "40px", height: "40px", objectFit: "cover" }}
//           />
//         ) : (
//           <div
//             className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
//             style={{ width: "40px", height: "40px" }}
//           >
//             {person.name.charAt(0)}
//           </div>
//         )}
//       </td> */}
//       <td>
//         <ImageWithActions
//           imageUrl={person.imageUrl}
//           name={person.name}
//           altText={person.name}
//         />
//       </td>
//       <td>
//         <div>
//           <strong>{person.name}</strong>
//           <div className="badge bg-primary">{person.batch}</div>
//         </div>
//       </td>
//       <td>
//         <div className="small">
//           <div>{person.mobile}</div>
//           <div className="text-truncate" style={{ maxWidth: "150px" }}>
//             {person.email}
//           </div>
//         </div>
//       </td>
//       <td>
//         <div className="d-flex flex-column gap-2">
//           {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//             <button
//               onClick={() => handleDeleteDirectory(person.id)}
//               className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
//               title="Delete"
//             >
//               <Trash2 size={14} />
//             </button>
//           )}
//           <button
//             onClick={() => handleViewDir(person.id)}
//             className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
//             title="View"
//           >
//             <Eye size={14} />
//           </button>
//         </div>
//       </td>
//     </tr>
//   );

//   const MobileParticipantsRow = ({ participant }) => (
//     <div className="card mb-3">
//       <div className="card-body">
//         <div className="row">
//           <div className="col-8">
//             <h6 className="mb-1">{participant.name}</h6>
//             <span className="badge bg-primary mb-2">{participant.batch}</span>
//             <div className="small">
//               <div>{participant.mobile}</div>
//               <div className="text-truncate" style={{ maxWidth: "200px" }}>
//                 {participant.email}
//               </div>
//               <div className="mt-2">
//                 <span
//                   className={`badge ${
//                     participant.paymentStatus === "Success" ||
//                     participant.paymentStatus === "Paid"
//                       ? "bg-success"
//                       : participant.paymentStatus === "Cancelled" ||
//                         participant.paymentStatus === "Failed" ||
//                         participant.paymentStatus === "Refunded"
//                       ? "bg-danger"
//                       : "bg-warning"
//                   }`}
//                 >
//                   {participant.paymentStatus === "Success" ||
//                   participant.paymentStatus === "Paid"
//                     ? "Success"
//                     : participant.paymentStatus === "Cancelled"
//                     ? "Cancelled"
//                     : participant.paymentStatus === "Failed"
//                     ? "Failed"
//                     : participant.paymentStatus === "Refunded"
//                     ? "Refunded"
//                     : "Pending"}
//                 </span>
//               </div>
//             </div>
//           </div>
//           <div className="col-4">
//             <div className="d-flex flex-column gap-2">
//               <div className="fw-bold">
//                 ৳{participant.totalAmount.toLocaleString()}
//               </div>
//               <div className="d-flex flex-column gap-1">
//                 <button
//                   onClick={() => handleViewPart(participant.id)}
//                   className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
//                   title="View"
//                 >
//                   <Eye size={14} />
//                 </button>
//                 {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//                   <button
//                     onClick={() => handleEditPart(participant.id)}
//                     className="btn btn-sm btn-outline-warning d-flex align-items-center justify-content-center"
//                     title="Edit"
//                   >
//                     <Edit2 size={14} />
//                   </button>
//                 )}
//                 {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//                   <button
//                     onClick={() => handleDelete(participant.id)}
//                     className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
//                     title="Delete"
//                   >
//                     <Trash2 size={14} />
//                   </button>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   const TabletParticipantsRow = ({ participant }) => (
//     <tr>
//       <td>
//         <div>
//           <strong>{participant.name}</strong>
//           <div className="small">{participant.email}</div>
//           <span className="badge bg-primary">{participant.batch}</span>
//         </div>
//       </td>
//       <td>
//         <span
//           className={`badge ${
//             participant.paymentStatus === "Success" ||
//             participant.paymentStatus === "Paid"
//               ? "bg-success"
//               : participant.paymentStatus === "Cancelled" ||
//                 participant.paymentStatus === "Failed" ||
//                 participant.paymentStatus === "Refunded"
//               ? "bg-danger"
//               : "bg-warning"
//           }`}
//         >
//           {participant.paymentStatus === "Success" ||
//           participant.paymentStatus === "Paid"
//             ? "Success"
//             : participant.paymentStatus === "Cancelled"
//             ? "Cancelled"
//             : participant.paymentStatus === "Failed"
//             ? "Failed"
//             : participant.paymentStatus === "Refunded"
//             ? "Refunded"
//             : "Pending"}
//         </span>
//         <div className="small mt-1">{participant.paymentMethod || "N/A"}</div>
//       </td>
//       <td className="fw-bold">৳{participant.totalAmount.toLocaleString()}</td>
//       <td>
//         <div className="d-flex gap-1 flex-wrap">
//           <button
//             onClick={() => handleViewPart(participant.id)}
//             className="btn btn-sm btn-outline-primary"
//             title="View"
//           >
//             <Eye size={14} />
//           </button>
//           {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//             <button
//               onClick={() => handleEditPart(participant.id)}
//               className="btn btn-sm btn-outline-warning"
//               title="Edit"
//             >
//               <Edit2 size={14} />
//             </button>
//           )}
//           {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//             <button
//               onClick={() => handleDelete(participant.id)}
//               className="btn btn-sm btn-outline-danger"
//               title="Delete"
//             >
//               <Trash2 size={14} />
//             </button>
//           )}
//           <div className="small text-muted mt-1 w-100">
//             {new Date(participant.registrationDate).toLocaleDateString()}
//           </div>
//         </div>
//       </td>
//     </tr>
//   );

//   return (
//     <div className="min-vh-100 bg-light">
//       <PaymentVerificationModal
//         isOpen={showPaymentVerificationModal}
//         onClose={() => setShowPaymentVerificationModal(false)}
//         API_BASE_URL={API_BASE_URL}
//         getAuthHeader={getAuthHeader}
//         fetchParticipants={fetchParticipants}
//       />
//       {/* Header */}
//       <header className="bg-dark text-white py-3">
//         <div className="container-fluid">
//           <div className="row align-items-center">
//             {/* Mobile Menu Button */}
//             <div className="col-auto d-lg-none">
//               <button
//                 className="btn btn-outline-light"
//                 onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
//               >
//                 {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
//               </button>
//             </div>

//             {/* Title */}
//             <div className="col">
//               <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center">
//                 <h2 className="mb-0 me-3">Admin Dashboard</h2>
//                 <small className="text-light opacity-75">
//                   Logged in as: <strong>{user?.username}</strong> ({user?.role})
//                 </small>
//               </div>
//             </div>

//             {/* Desktop Actions */}
//             <div className="col-auto d-none d-lg-block">
//               <div className="d-flex gap-2">
//                 <button
//                   className="btn btn-primary d-flex align-items-center gap-2"
//                   onClick={handleHome}
//                 >
//                   <HomeIcon size={18} />
//                   View Site
//                 </button>
//                 {user?.role === "ADMIN" && (
//                   <button
//                     className={`btn d-flex align-items-center gap-2 ${
//                       registrationActive ? "btn-danger" : "btn-success"
//                     }`}
//                     onClick={toggleRegistrationStatus}
//                     disabled={updatingStatus}
//                   >
//                     {registrationActive ? (
//                       <ToggleLeft size={18} />
//                     ) : (
//                       <ToggleRight size={18} />
//                     )}
//                     {updatingStatus
//                       ? "Updating..."
//                       : registrationActive
//                       ? "Disable"
//                       : "Enable"}
//                   </button>
//                 )}
//                 {user?.role === "ADMIN" && (
//                   <button
//                     className="btn btn-info d-flex align-items-center gap-2 text-white"
//                     onClick={() => setShowPaymentVerificationModal(true)}
//                   >
//                     <CheckCircle size={18} />
//                     Payment Verification
//                   </button>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Mobile Actions (Collapsible) */}
//           {isMobileMenuOpen && (
//             <div className="row mt-3 d-lg-none">
//               <div className="col-12">
//                 <div className="d-flex flex-column gap-2">
//                   <button
//                     className="btn btn-primary d-flex align-items-center gap-2 justify-content-center"
//                     onClick={handleHome}
//                   >
//                     <HomeIcon size={18} />
//                     View Site
//                   </button>
//                   {user?.role === "ADMIN" && (
//                     <button
//                       className={`btn d-flex align-items-center gap-2 justify-content-center ${
//                         registrationActive ? "btn-danger" : "btn-success"
//                       }`}
//                       onClick={toggleRegistrationStatus}
//                       disabled={updatingStatus}
//                     >
//                       {registrationActive ? (
//                         <ToggleLeft size={18} />
//                       ) : (
//                         <ToggleRight size={18} />
//                       )}
//                       {updatingStatus
//                         ? "Updating..."
//                         : registrationActive
//                         ? "Disable Registration"
//                         : "Enable Registration"}
//                     </button>
//                   )}
//                   {user?.role === "ADMIN" && (
//                     <button
//                       className="btn btn-info d-flex align-items-center gap-2 justify-content-center text-white"
//                       onClick={() => setShowPaymentVerificationModal(true)}
//                     >
//                       <CheckCircle size={18} />
//                       Payment Verification
//                     </button>
//                   )}
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Tab Navigation */}
//           <div className="row mt-3">
//             <div className="col-12">
//               <div className="d-flex gap-2">
//                 <button
//                   className={`btn d-flex align-items-center justify-content-between flex-fill ${
//                     activeTab === "directory" ? "btn-search" : "btn-success"
//                   }`}
//                   onClick={() => setActiveTab("directory")}
//                   style={{ minHeight: "60px" }}
//                 >
//                   <div
//                     className={`${
//                       activeTab === "directory" ? "fw-bolder text-xl" : ""
//                     }`}
//                   >
//                     Directory
//                   </div>
//                   <div className="h4 mb-0">{directories.length}</div>
//                 </button>
//                 <button
//                   className={`btn d-flex align-items-center justify-content-between flex-fill ${
//                     activeTab === "participants" ? "btn-search" : "btn-success"
//                   }`}
//                   onClick={() => setActiveTab("participants")}
//                   style={{ minHeight: "60px" }}
//                 >
//                   <div
//                     className={`${
//                       activeTab === "participants" ? "fw-bolder text-xl" : ""
//                     }`}
//                   >
//                     Participants
//                   </div>
//                   <div className="text-start">
//                     <div className="h4 mb-0">{participants.length}</div>
//                     <div className="small opacity-75">
//                       Paid: <span className="fw-bold">{completedPayments}</span>
//                     </div>
//                   </div>
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       </header>

//       {/* Main Content */}
//       <main className="container-fluid py-3">
//         {/* Directory Tab */}
//         {activeTab === "directory" && (
//           <div className="card shadow-sm">
//             <div className="card-body">
//               {/* Header with Actions */}
//               <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
//                 <h2 className="mb-0">Directory List</h2>
//                 <div className="d-flex flex-wrap gap-2">
//                   {/* Mobile Filter Toggle */}
//                   <button
//                     className="btn btn-outline-secondary d-md-none"
//                     onClick={() => setIsFilterOpen(!isFilterOpen)}
//                   >
//                     <Filter size={18} />
//                     Filters
//                   </button>
//                   {/* Refresh Button for Directory */}
//                   <button
//                     className="btn btn-outline-primary d-flex align-items-center gap-2"
//                     onClick={refreshDirectoryData}
//                     disabled={refreshingDirectory}
//                   >
//                     <RefreshCw
//                       size={18}
//                       className={refreshingDirectory ? "spin" : ""}
//                     />
//                     <span className="d-none d-sm-inline">
//                       {refreshingDirectory ? "Refreshing..." : "Refresh"}
//                     </span>
//                   </button>
//                   <button
//                     className="btn btn-success d-flex align-items-center gap-2"
//                     onClick={downloadDirectoryExcel}
//                   >
//                     <Download size={18} />
//                     <span className="d-none d-sm-inline">Export</span>
//                   </button>
//                 </div>
//               </div>

//               {/* Filters */}
//               <div
//                 className={`card mb-3 ${
//                   isMobile && !isFilterOpen ? "d-none" : ""
//                 }`}
//               >
//                 <div className="card-body">
//                   <div className="row g-3">
//                     {/* Search */}
//                     <div className="col-12 col-md-6">
//                       <div className="input-group">
//                         <span className="input-group-text">
//                           <Search size={16} />
//                         </span>
//                         <input
//                           type="text"
//                           className="form-control"
//                           placeholder="Search directory..."
//                           value={directorySearchTerm}
//                           onChange={(e) =>
//                             setDirectorySearchTerm(e.target.value)
//                           }
//                         />
//                         {directorySearchTerm && (
//                           <button
//                             className="btn btn-outline-secondary"
//                             type="button"
//                             onClick={() => setDirectorySearchTerm("")}
//                           >
//                             <X size={16} />
//                           </button>
//                         )}
//                       </div>
//                     </div>

//                     {/* Batch Filter */}
//                     <div className="col-12 col-md-3">
//                       <select
//                         className="form-select"
//                         value={batchFilter}
//                         onChange={(e) => setBatchFilter(e.target.value)}
//                       >
//                         <option value="">All Groups</option>
//                         {uniqueBatches.map((batch) => (
//                           <option key={batch} value={batch}>
//                             {batch}
//                           </option>
//                         ))}
//                       </select>
//                     </div>

//                     {/* Blood Group Filter */}
//                     <div className="col-12 col-md-3">
//                       <select
//                         className="form-select"
//                         value={bloodGroupFilter}
//                         onChange={(e) => setBloodGroupFilter(e.target.value)}
//                       >
//                         <option value="">All Blood Groups</option>
//                         {uniqueBloodGroups.map((group) => (
//                           <option key={group} value={group}>
//                             {group}
//                           </option>
//                         ))}
//                       </select>
//                     </div>

//                     {/* Clear Filters */}
//                     {(batchFilter ||
//                       bloodGroupFilter ||
//                       directorySearchTerm) && (
//                       <div className="col-12">
//                         <button
//                           className="btn btn-sm btn-outline-secondary"
//                           onClick={() => {
//                             setBatchFilter("");
//                             setBloodGroupFilter("");
//                             setDirectorySearchTerm("");
//                           }}
//                         >
//                           Clear All Filters
//                         </button>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>

//               {/* Directory Content */}
//               {filteredDirectories.length > 0 ? (
//                 <>
//                   {/* Mobile View */}
//                   {isMobile ? (
//                     <div>
//                       {filteredDirectories.map((person) => (
//                         <MobileDirectoryRow key={person.id} person={person} />
//                       ))}
//                     </div>
//                   ) : /* Tablet View */
//                   isTablet ? (
//                     <div className="table-responsive">
//                       <table className="table table-hover">
//                         <thead className="table-light">
//                           <tr>
//                             {getDirectoryTableHeaders().map((header) => (
//                               <th key={header.key} className={header.className}>
//                                 {header.label}
//                               </th>
//                             ))}
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {filteredDirectories.map((person) => (
//                             <TabletDirectoryRow
//                               key={person.id}
//                               person={person}
//                             />
//                           ))}
//                         </tbody>
//                       </table>
//                     </div>
//                   ) : (
//                     /* Desktop View */
//                     <div className="table-responsive">
//                       <table className="table table-hover">
//                         <thead className="table-light">
//                           <tr>
//                             {getDirectoryTableHeaders().map((header) => (
//                               <th key={header.key}>{header.label}</th>
//                             ))}
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {filteredDirectories.map((person) => (
//                             <tr key={person.id}>
//                               {/* <td style={{ width: "80px" }}>
//                                 {person.imageUrl ? (
//                                   <img
//                                     src={`${API_BASE_URL}${person.imageUrl}`}
//                                     alt={person.name}
//                                     className="rounded-circle"
//                                     style={{
//                                       width: "60px",
//                                       height: "60px",
//                                       objectFit: "cover",
//                                     }}
//                                   />
//                                 ) : (
//                                   <div
//                                     className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
//                                     style={{ width: "60px", height: "60px" }}
//                                   >
//                                     {person.name.charAt(0)}
//                                   </div>
//                                 )}
//                               </td> */}
//                               <td style={{ width: "80px" }}>
//                                 <ImageWithActions
//                                   imageUrl={person.imageUrl}
//                                   name={person.name}
//                                   altText={person.name}
//                                 />
//                               </td>
//                               <td>
//                                 <div>
//                                   <div>
//                                     <strong>{person.name}</strong>
//                                   </div>
//                                   <div className="badge bg-primary">
//                                     {person.batch}
//                                   </div>
//                                 </div>
//                               </td>
//                               <td>
//                                 <div className="small">
//                                   <div>{person.mobile}</div>
//                                   <div>{person.email}</div>
//                                   <div className="text-muted">
//                                     {person.permanentAddress}
//                                   </div>
//                                 </div>
//                               </td>
//                               <td>
//                                 <div>
//                                   <strong>{person.profession}</strong>
//                                   <div className="small">
//                                     {person.officeAddress}
//                                   </div>
//                                 </div>
//                               </td>
//                               <td>
//                                 {person.bloodGroup && (
//                                   <span
//                                     className={`badge ${
//                                       person.bloodGroup?.includes("+")
//                                         ? "bg-danger"
//                                         : "bg-warning"
//                                     }`}
//                                   >
//                                     {person.bloodGroup}
//                                   </span>
//                                 )}
//                               </td>
//                               <td>
//                                 <div className="d-flex gap-2">
//                                   {(user?.role === "ADMIN" ||
//                                     user?.role === "EDITOR") && (
//                                     <button
//                                       onClick={() =>
//                                         handleDeleteDirectory(person.id)
//                                       }
//                                       className="btn btn-sm btn-outline-danger"
//                                       title="Delete"
//                                     >
//                                       <Trash2 size={16} />
//                                     </button>
//                                   )}
//                                   <button
//                                     onClick={() => handleViewDir(person.id)}
//                                     className="btn btn-sm btn-outline-primary"
//                                     title="View"
//                                   >
//                                     <Eye size={16} />
//                                   </button>
//                                 </div>
//                               </td>
//                             </tr>
//                           ))}
//                         </tbody>
//                       </table>
//                     </div>
//                   )}

//                   {/* Footer Stats */}
//                   <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 pt-3 border-top">
//                     <div className="small text-muted mb-2 mb-md-0">
//                       Showing {filteredDirectories.length} of{" "}
//                       {directories.length} entries
//                     </div>
//                     <div className="small text-muted">
//                       Updated: {new Date().toLocaleTimeString()}
//                     </div>
//                   </div>
//                 </>
//               ) : (
//                 <div className="text-center py-5">
//                   <div className="text-muted">
//                     <i className="bi bi-people display-6 d-block mb-2"></i>
//                     No directory entries found
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         )}

//         {/* Participants Tab */}
//         {activeTab === "participants" && (
//           <div className="card shadow-sm">
//             <div className="card-body">
//               {/* Header with Actions */}
//               <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
//                 <h2 className="mb-0">Participant List</h2>
//                 <div className="d-flex flex-wrap gap-2">
//                   {/* Mobile Search */}
//                   <div
//                     className="input-group d-md-none"
//                     style={{ minWidth: "250px" }}
//                   >
//                     <span className="input-group-text">
//                       <Search size={16} />
//                     </span>
//                     <input
//                       type="text"
//                       className="form-control"
//                       placeholder="Search..."
//                       value={participantSearchTerm}
//                       onChange={(e) => setParticipantSearchTerm(e.target.value)}
//                     />
//                   </div>
//                   {/* Refresh Button for Participants */}
//                   <button
//                     className="btn btn-outline-primary d-flex align-items-center gap-2"
//                     onClick={refreshParticipantsData}
//                     disabled={refreshingParticipants}
//                   >
//                     <RefreshCw
//                       size={18}
//                       className={refreshingParticipants ? "spin" : ""}
//                     />
//                     <span className="d-none d-sm-inline">
//                       {refreshingParticipants ? "Refreshing..." : "Refresh"}
//                     </span>
//                   </button>
//                   <button
//                     className="btn btn-success d-flex align-items-center gap-2"
//                     onClick={downloadExcel}
//                   >
//                     <Download size={18} />
//                     <span className="d-none d-sm-inline">Export</span>
//                   </button>
//                 </div>
//               </div>

//               {/* Desktop Search */}
//               <div className="d-none d-md-block mb-3">
//                 <div className="row">
//                   <div className="col-md-6">
//                     <div className="input-group">
//                       <span className="input-group-text">
//                         <Search size={16} />
//                       </span>
//                       <input
//                         type="text"
//                         className="form-control"
//                         placeholder="Search participants..."
//                         value={participantSearchTerm}
//                         onChange={(e) =>
//                           setParticipantSearchTerm(e.target.value)
//                         }
//                       />
//                       {participantSearchTerm && (
//                         <button
//                           className="btn btn-outline-secondary"
//                           onClick={() => setParticipantSearchTerm("")}
//                         >
//                           <X size={16} />
//                         </button>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Participants Content */}
//               {filteredParticipants.length > 0 ? (
//                 <>
//                   {/* Mobile View */}
//                   {isMobile ? (
//                     <div>
//                       {filteredParticipants.map((participant) => (
//                         <MobileParticipantsRow
//                           key={participant.id}
//                           participant={participant}
//                         />
//                       ))}
//                     </div>
//                   ) : /* Tablet View */
//                   isTablet ? (
//                     <div className="table-responsive">
//                       <table className="table table-hover">
//                         <thead className="table-light">
//                           <tr>
//                             {getParticipantsTableHeaders().map((header) => (
//                               <th key={header.key} className={header.className}>
//                                 {header.label}
//                               </th>
//                             ))}
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {filteredParticipants.map((participant) => (
//                             <TabletParticipantsRow
//                               key={participant.id}
//                               participant={participant}
//                             />
//                           ))}
//                         </tbody>
//                       </table>
//                     </div>
//                   ) : (
//                     /* Desktop View */
//                     <div className="table-responsive">
//                       <table className="table table-hover">
//                         <thead className="table-light">
//                           <tr>
//                             {getParticipantsTableHeaders().map((header) => (
//                               <th key={header.key}>{header.label}</th>
//                             ))}
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {filteredParticipants.map((p) => (
//                             <tr key={p.id}>
//                               <td>
//                                 <div className="fw-bold">{p.name}</div>
//                                 <div className="small text-muted">
//                                   {p.email}
//                                 </div>
//                               </td>
//                               <td>
//                                 <span className="badge bg-primary">
//                                   {p.batch}
//                                 </span>
//                               </td>
//                               <td>
//                                 <div>{p.mobile}</div>
//                                 <div className="small text-muted">
//                                   T-Shirt: {p.tshirtSize}
//                                 </div>
//                               </td>
//                               <td>
//                                 <span
//                                   className={`badge ${
//                                     p.paymentStatus === "Success" ||
//                                     p.paymentStatus === "Paid"
//                                       ? "bg-success"
//                                       : p.paymentStatus === "Cancelled" ||
//                                         p.paymentStatus === "Failed" ||
//                                         p.paymentStatus === "Refunded"
//                                       ? "bg-danger"
//                                       : "bg-warning"
//                                   }`}
//                                 >
//                                   {p.paymentStatus === "Success" ||
//                                   p.paymentStatus === "Paid"
//                                     ? "Success"
//                                     : p.paymentStatus === "Cancelled"
//                                     ? "Cancelled"
//                                     : p.paymentStatus === "Failed"
//                                     ? "Failed"
//                                     : p.paymentStatus === "Refunded"
//                                     ? "Refunded"
//                                     : "Pending"}
//                                 </span>
//                               </td>
//                               <td>
//                                 <div className="small">
//                                   <div>
//                                     <strong>Order:</strong> {p.orderId || "N/A"}
//                                   </div>
//                                   <div>
//                                     <strong>Txn:</strong>{" "}
//                                     {p.transactionId || "N/A"}
//                                   </div>
//                                   <div>
//                                     <strong>Bank:</strong>{" "}
//                                     {p.bankTransactionId || "N/A"}
//                                   </div>
//                                 </div>
//                               </td>
//                               <td>{p.paymentMethod || "N/A"}</td>
//                               <td className="fw-bold">
//                                 ৳{p.totalAmount.toLocaleString()}
//                               </td>
//                               <td>
//                                 <div className="small">
//                                   {new Date(
//                                     p.registrationDate
//                                   ).toLocaleDateString()}
//                                 </div>
//                               </td>
//                               <td>
//                                 <div className="d-flex gap-1">
//                                   {(user?.role === "ADMIN" ||
//                                     user?.role === "EDITOR") && (
//                                     <button
//                                       onClick={() => handleDelete(p.id)}
//                                       className="btn btn-sm btn-outline-danger"
//                                       title="Delete"
//                                     >
//                                       <Trash2 size={16} />
//                                     </button>
//                                   )}
//                                   <button
//                                     onClick={() => handleViewPart(p.id)}
//                                     className="btn btn-sm btn-outline-primary"
//                                     title="View"
//                                   >
//                                     <Eye size={16} />
//                                   </button>
//                                   {(user?.role === "ADMIN" ||
//                                     user?.role === "EDITOR") && (
//                                     <button
//                                       onClick={() => handleEditPart(p.id)}
//                                       className="btn btn-sm btn-outline-warning"
//                                       title="Edit"
//                                     >
//                                       <Edit2 size={16} />
//                                     </button>
//                                   )}
//                                 </div>
//                               </td>
//                             </tr>
//                           ))}
//                         </tbody>
//                       </table>
//                     </div>
//                   )}

//                   {/* Footer Stats */}
//                   <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 pt-3 border-top">
//                     <div className="small text-muted mb-2 mb-md-0">
//                       Showing {filteredParticipants.length} of{" "}
//                       {participants.length} participants
//                       {participantSearchTerm &&
//                         ` matching "${participantSearchTerm}"`}
//                     </div>
//                     <div className="small text-muted">
//                       Last updated: {new Date().toLocaleTimeString()}
//                     </div>
//                   </div>
//                 </>
//               ) : (
//                 <div className="text-center py-5">
//                   <div className="text-muted">
//                     <i className="bi bi-person display-6 d-block mb-2"></i>
//                     No participants found
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         )}
//       </main>

//       <style jsx>{`
//         @media (max-width: 768px) {
//           .table-responsive {
//             font-size: 0.875rem;
//           }
//           .btn {
//             padding: 0.375rem 0.75rem;
//             font-size: 0.875rem;
//           }
//         }
//         @media (max-width: 576px) {
//           .card-body {
//             padding: 1rem;
//           }
//           h2 {
//             font-size: 1.5rem;
//           }
//         }

//         @keyframes spin {
//           from {
//             transform: rotate(0deg);
//           }
//           to {
//             transform: rotate(360deg);
//           }
//         }

//         .spin {
//           animation: spin 1s linear infinite;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default AdminPage;
